import React, { useMemo } from "react";
import { Dropdown, DropdownListItem } from "cerulean";
import { TruncatedAccount } from "@narmi/design_system";
import Account from "byzantine/src/Account";
import { useLocalization } from "@fluent/react";

interface AccountSelectorProps {
  accounts: Account[];
  onChange: (accountId: string[]) => void;
  value: string[] | undefined;
  error?: string;
  errorText?: string;
  selectAll?: boolean;
}

const AccountSelector = ({
  accounts,
  onChange,
  value,
  error,
  errorText,
  selectAll = true,
}: AccountSelectorProps) => {
  const { l10n } = useLocalization();
  const displayValue = useMemo(() => {
    if (!value) return "";

    const selectedAccount = accounts.filter((account) =>
      value.includes(account.id)
    );

    if (selectedAccount.length > 1) {
      return l10n.getString(
        "custom-alerts-select-label-select-all",
        {},
        "All accounts selected"
      );
    } else if (selectedAccount.length === 1) {
      return selectedAccount[0].getDescription();
    }
    return "";
  }, [l10n, value, accounts]);

  return (
    <Dropdown
      triggerLabel={l10n.getString(
        "custom-alerts-form-label-account",
        {},
        "Account name"
      )}
      triggerValue={displayValue}
      error={error || errorText || ""}
    >
      <div className="margin--s" />
      {selectAll && (
        <DropdownListItem
          key={"select-all"}
          anyItemSelected={!!value && value.length > 0}
          isSelected={!!value && value.length === accounts.length}
          onClick={() => onChange(accounts.map((account) => account.id))}
        >
          {l10n.getString(
            "custom-alerts-select-option-select-all",
            {},
            "Select all"
          )}
        </DropdownListItem>
      )}
      {accounts.map((account) => {
        const { name, endingValue } = account.getTruncatedValues();
        return (
          <DropdownListItem
            key={account.id}
            anyItemSelected={!!value && value.length > 0}
            isSelected={!!value && value.includes(account.id)}
            onClick={() => onChange([account.id])}
          >
            <TruncatedAccount name={name} lastFour={endingValue} />
          </DropdownListItem>
        );
      })}
    </Dropdown>
  );
};

export default AccountSelector;
